import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
	useSpheraSettings,
	useUpdateSpheraSettings,
} from '@/api/queries/sphera-export-queries'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	SPHERA_INSPECTION_TYPES,
	type SpheraInspectionType,
	type SpheraSettings,
	type SpheraSettingsOutput,
} from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

type Choice = 'observationTypes' | 'employeeTypes' | 'potentialRisks'

interface FieldDef {
	path: [keyof SpheraSettings, string?]
	choice?: Choice
	numeric?: boolean
}

const FIELD_GROUPS: { id: string; fields: FieldDef[] }[] = [
	{
		id: 'identification',
		fields: [
			{ path: ['incidentIdPrefix'] },
			{ path: ['maxRowsPerFile'], numeric: true },
		],
	},
	{
		id: 'fixed',
		fields: [
			{ path: ['fixed', 'typeOfLocation'] },
			{ path: ['fixed', 'potentialRisk'], choice: 'potentialRisks' },
			{ path: ['fixed', 'owner'] },
			{ path: ['fixed', 'imported'] },
			{ path: ['fixed', 'formId'] },
			{ path: ['fixed', 'isCommitted'] },
			{ path: ['fixed', 'closed'] },
			{ path: ['fixed', 'status'] },
			{ path: ['fixed', 'eventType'] },
		],
	},
	{
		id: 'observationType',
		fields: [
			{
				path: ['observationType', 'cartaoAmarelo'],
				choice: 'observationTypes',
			},
			{
				path: ['observationType', 'interacaoCongratulated'],
				choice: 'observationTypes',
			},
			{
				path: ['observationType', 'interacaoOriented'],
				choice: 'observationTypes',
			},
			{
				path: ['observationType', 'vereAgirStopAndFix'],
				choice: 'observationTypes',
			},
			{
				path: ['observationType', 'vereAgirFixAndContinue'],
				choice: 'observationTypes',
			},
		],
	},
	{
		id: 'interacaoActionText',
		fields: [
			{ path: ['interacaoActionText', 'congratulated'] },
			{ path: ['interacaoActionText', 'oriented'] },
		],
	},
	{
		id: 'employeeType',
		fields: [
			{ path: ['employeeType', 'cartaoAmarelo'], choice: 'employeeTypes' },
			{ path: ['employeeType', 'noGroup'], choice: 'employeeTypes' },
			{ path: ['employeeType', 'ssma'], choice: 'employeeTypes' },
			{ path: ['employeeType', 'other'], choice: 'employeeTypes' },
			{ path: ['employeeType', 'ssmaClusterName'] },
		],
	},
]

function readValue(settings: SpheraSettings, [group, key]: FieldDef['path']) {
	const value = settings[group] as unknown
	return String(key ? (value as Record<string, string>)[key] : value)
}

function writeValue(
	settings: SpheraSettings,
	[group, key]: FieldDef['path'],
	value: string | number,
): SpheraSettings {
	if (!key) return { ...settings, [group]: value }
	return {
		...settings,
		[group]: { ...(settings[group] as object), [key]: value },
	}
}

function FieldsTable({ data }: { data: SpheraSettingsOutput }) {
	const { t } = useTranslation()
	const [exampleType, setExampleType] =
		useState<SpheraInspectionType>('VER_E_AGIR')
	const example = data.examples[exampleType]

	return (
		<div className="space-y-2 pb-2">
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
				<p className="text-xs text-muted-foreground">
					{t('administration.spheraOptions.params.fieldsHint')}
				</p>
				<div className="sm:w-56">
					<NativeSelect
						value={exampleType}
						onChange={(e) =>
							setExampleType(e.target.value as SpheraInspectionType)
						}
					>
						{SPHERA_INSPECTION_TYPES.map((type) => (
							<NativeSelectOption key={type} value={type}>
								{t(`administration.spheraExport.types.${type}`)}
							</NativeSelectOption>
						))}
					</NativeSelect>
				</div>
			</div>
			<div className="overflow-x-auto rounded-md border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>
								{t('administration.spheraOptions.params.column')}
							</TableHead>
							<TableHead>
								{t('administration.spheraOptions.params.source')}
							</TableHead>
							<TableHead>
								{t('administration.spheraOptions.params.example')}
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{data.columns.map((column) => (
							<TableRow key={column.key}>
								<TableCell className="whitespace-nowrap">
									<p className="text-sm font-medium text-kaefer-dark-grey">
										{column.label}
									</p>
									<p className="text-xs text-muted-foreground">
										{column.header}
									</p>
								</TableCell>
								<TableCell className="min-w-64 whitespace-normal text-sm">
									{t(
										`administration.spheraOptions.params.sources.${column.key}`,
									)}
								</TableCell>
								<TableCell
									className="max-w-64 truncate text-sm"
									title={example?.[column.key]}
								>
									{example ? (
										example[column.key] || (
											<span className="text-muted-foreground">—</span>
										)
									) : (
										<span className="text-muted-foreground">
											{t('administration.spheraOptions.params.noExample')}
										</span>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}

function GroupSummary({
	settings,
	fields,
}: {
	settings: SpheraSettings
	fields: FieldDef[]
}) {
	const values = fields
		.slice(0, 2)
		.map((field) => readValue(settings, field.path))
	return (
		<span className="min-w-0 truncate text-xs font-normal text-muted-foreground">
			{values.join(' · ')}
			{fields.length > 2 ? ' …' : ''}
		</span>
	)
}

function SettingsGroups({
	data,
	draft,
	setDraft,
}: {
	data: SpheraSettingsOutput
	draft: SpheraSettings
	setDraft: (update: (prev: SpheraSettings) => SpheraSettings) => void
}) {
	const { t } = useTranslation()

	return (
		<>
			{FIELD_GROUPS.map((group) => (
				<AccordionItem
					key={group.id}
					value={group.id}
					className="rounded-md border px-3"
				>
					<AccordionTrigger className="py-3 text-sm">
						<span className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
							<span>
								{t(`administration.spheraOptions.params.groups.${group.id}`)}
							</span>
							<GroupSummary settings={draft} fields={group.fields} />
						</span>
					</AccordionTrigger>
					<AccordionContent>
						<div className="grid gap-3 pb-2 md:grid-cols-2">
							{group.fields.map((field) => {
								const id = field.path.filter(Boolean).join('_')
								const value = readValue(draft, field.path)
								const onChange = (next: string) =>
									setDraft((prev) =>
										writeValue(
											prev,
											field.path,
											field.numeric ? Number(next) : next,
										),
									)
								return (
									<label
										key={id}
										className="flex flex-col gap-1 text-sm text-kaefer-dark-grey"
									>
										{t(`administration.spheraOptions.params.fields.${id}`)}
										{field.choice ? (
											<NativeSelect
												value={value}
												onChange={(e) => onChange(e.target.value)}
											>
												{data.choices[field.choice].map((choice) => (
													<NativeSelectOption key={choice} value={choice}>
														{choice}
													</NativeSelectOption>
												))}
											</NativeSelect>
										) : (
											<Input
												value={value}
												onChange={(e) => onChange(e.target.value)}
												{...(field.numeric
													? { type: 'number', min: 1, max: 2000 }
													: {})}
											/>
										)}
									</label>
								)
							})}
						</div>
					</AccordionContent>
				</AccordionItem>
			))}
		</>
	)
}

function SettingsEditor({ data }: { data: SpheraSettingsOutput }) {
	const { t } = useTranslation()
	const [draft, setDraft] = useState<SpheraSettings>(data.settings)
	const mutation = useUpdateSpheraSettings()
	const dirty = JSON.stringify(draft) !== JSON.stringify(data.settings)

	const save = async () => {
		try {
			await mutation.mutateAsync(draft)
			toast.success(t('administration.spheraOptions.params.saved'))
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<div className="space-y-3">
			<div className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2">
				<p className="text-xs text-muted-foreground">
					{dirty
						? t('administration.spheraOptions.params.unsaved')
						: t('administration.spheraOptions.params.intro')}
				</p>
				<div className="flex shrink-0 gap-2">
					<Button
						size="sm"
						variant="outline"
						className="w-auto"
						disabled={!dirty || mutation.isPending}
						onClick={() => setDraft(data.settings)}
					>
						{t('administration.spheraOptions.params.discard')}
					</Button>
					<Button
						size="sm"
						className="w-auto"
						disabled={!dirty || mutation.isPending}
						onClick={save}
					>
						{t('administration.spheraOptions.save')}
					</Button>
				</div>
			</div>

			<Accordion type="multiple" defaultValue={['fixed']} className="space-y-2">
				<SettingsGroups data={data} draft={draft} setDraft={setDraft} />
				<AccordionItem value="fields" className="rounded-md border px-3">
					<AccordionTrigger className="py-3 text-sm">
						{t('administration.spheraOptions.params.fieldsTitle')}
					</AccordionTrigger>
					<AccordionContent>
						<FieldsTable data={data} />
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</div>
	)
}

/** Como cada coluna do arquivo é preenchida (com um exemplo real) e os valores fixos/regras editáveis. */
export function SpheraSettingsTab() {
	const { t } = useTranslation()
	const { data, isLoading } = useSpheraSettings()

	if (isLoading || !data) {
		return (
			<p className="p-6 text-center text-sm text-muted-foreground">
				{t('common.loading')}
			</p>
		)
	}

	return (
		// key: recarrega o formulário com o que foi salvo
		<SettingsEditor data={data} key={JSON.stringify(data.settings)} />
	)
}
